import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';

import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { violationChargeService } from '../../services/violationChargeService';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import ViolationChargeCard from '../owner-dashboard/components/ViolationChargeCard';
import { supabase } from '../../lib/supabase';


const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const PaymentForm = ({ charge, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const { error: submitError } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/renter-dashboard?payment=success`,
        },
      });

      if (submitError) {
        setError(submitError?.message);
      } else {
        onSuccess?.();
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err?.message || 'Payment failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-danger/10 border border-danger/20 rounded-lg p-4 flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} className="text-danger flex-shrink-0 mt-0.5" strokeWidth={2} />
          <p className="font-body text-sm text-danger">{error}</p>
        </div>
      )}

      <PaymentElement />

      <div className="flex items-center justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isProcessing}
          disabled={!stripe || isProcessing}
          iconName="CreditCard"
        >
          Pay ${Number(charge?.chargeAmount)?.toFixed(2)}
        </Button>
      </div>
    </form>
  );
};

const RenterViolationCharges = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [charges, setCharges] = useState([]);
  const [isLoadingCharges, setIsLoadingCharges] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchCharges = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingCharges(true);
        const data = await violationChargeService?.getRenterCharges();
        setCharges(data || []);
      } catch (error) {
        console.error('Error fetching charges:', error);
      } finally {
        setIsLoadingCharges(false);
      }
    };

    if (isAuthenticated) {
      fetchCharges();
    }
  }, [user, isAuthenticated]);

  // Real-time subscription for violation charges
  useEffect(() => {
    if (!user?.id) return;

    const chargesChannel = supabase
      ?.channel('renter_violation_charges')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rental_violation_charges',
          filter: `renter_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            // Add new charge to the list
            setCharges((prev) => [payload?.new, ...prev]);
          } else if (payload?.eventType === 'UPDATE') {
            // Update existing charge
            setCharges((prev) =>
              prev?.map((charge) =>
                charge?.id === payload?.new?.id ? payload?.new : charge
              )
            );
          } else if (payload?.eventType === 'DELETE') {
            // Remove deleted charge
            setCharges((prev) => prev?.filter((c) => c?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(chargesChannel);
    };
  }, [user?.id]);

  const handlePayCharge = async (charge) => {
    try {
      setSelectedCharge(charge);
      
      // Process charge payment to get client secret
      const result = await violationChargeService?.processChargePayment(charge?.id);
      setPaymentClientSecret(result?.paymentIntent?.clientSecret);
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert(error?.message || 'Failed to initiate payment');
    }
  };

  const handleDisputeCharge = (charge) => {
    navigate(`/charge-dispute?chargeId=${charge?.id}`);
  };

  const handleSubmitDispute = async () => {
    if (!disputeReason?.trim()) {
      alert('Please provide a reason for the dispute');
      return;
    }

    try {
      setIsSubmittingDispute(true);
      await violationChargeService?.updateChargeStatus(
        selectedCharge?.id,
        'disputed',
        disputeReason
      );

      // Update local state
      setCharges(prev =>
        prev?.map(c =>
          c?.id === selectedCharge?.id
            ? { ...c, chargeStatus: 'disputed', disputeReason }
            : c
        )
      );

      setShowDisputeModal(false);
      setDisputeReason('');
      setSelectedCharge(null);
    } catch (error) {
      console.error('Error submitting dispute:', error);
      alert(error?.message || 'Failed to submit dispute');
    } finally {
      setIsSubmittingDispute(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setPaymentClientSecret(null);
    setSelectedCharge(null);
    // Refresh charges
    window.location?.reload();
  };

  if (loading || isLoadingCharges) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const pendingCharges = charges?.filter(c => c?.chargeStatus === 'pending');
  const paidCharges = charges?.filter(c => c?.chargeStatus === 'paid');
  const disputedCharges = charges?.filter(c => c?.chargeStatus === 'disputed');

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Violation Charges
            </h1>
            <p className="font-body text-muted-foreground">
              Review and manage charges from trailer owners for rental violations.
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-card rounded-lg shadow-elevation-2 p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-body font-semibold text-muted-foreground">Pending</h3>
                <Icon name="Clock" size={20} className="text-warning" strokeWidth={2} />
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {pendingCharges?.length}
              </p>
              <p className="font-caption text-sm text-muted-foreground mt-1">
                ${pendingCharges?.reduce((sum, c) => sum + Number(c?.chargeAmount), 0)?.toFixed(2)} total
              </p>
            </div>

            <div className="bg-card rounded-lg shadow-elevation-2 p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-body font-semibold text-muted-foreground">Paid</h3>
                <Icon name="CheckCircle" size={20} className="text-success" strokeWidth={2} />
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {paidCharges?.length}
              </p>
              <p className="font-caption text-sm text-muted-foreground mt-1">
                ${paidCharges?.reduce((sum, c) => sum + Number(c?.chargeAmount), 0)?.toFixed(2)} total
              </p>
            </div>

            <div className="bg-card rounded-lg shadow-elevation-2 p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-body font-semibold text-muted-foreground">Disputed</h3>
                <Icon name="AlertTriangle" size={20} className="text-danger" strokeWidth={2} />
              </div>
              <p className="font-heading text-3xl font-bold text-foreground">
                {disputedCharges?.length}
              </p>
              <p className="font-caption text-sm text-muted-foreground mt-1">
                Under review
              </p>
            </div>
          </div>

          {/* Charges List */}
          {charges?.length > 0 ? (
            <div className="space-y-6">
              {/* Pending Charges */}
              {pendingCharges?.length > 0 && (
                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
                    Pending Charges
                  </h2>
                  <div className="space-y-4">
                    {pendingCharges?.map((charge) => (
                      <div key={charge?.id} className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
                        <ViolationChargeCard charge={charge} onStatusUpdate={() => {}} />
                        <div className="border-t border-border p-4 bg-muted/30 flex items-center justify-end space-x-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDisputeCharge(charge)}
                            iconName="AlertTriangle"
                          >
                            Dispute Charge
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handlePayCharge(charge)}
                            iconName="CreditCard"
                          >
                            Pay Now
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Other Charges */}
              {(paidCharges?.length > 0 || disputedCharges?.length > 0) && (
                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
                    Charge History
                  </h2>
                  <div className="space-y-4">
                    {[...disputedCharges, ...paidCharges]?.map((charge) => (
                      <ViolationChargeCard key={charge?.id} charge={charge} onStatusUpdate={() => {}} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-elevation-2 p-12 text-center">
              <Icon name="DollarSign" size={64} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                No Violation Charges
              </h3>
              <p className="font-body text-muted-foreground">
                You don't have any violation charges at this time.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && paymentClientSecret && selectedCharge && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-elevation-4 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-foreground">
                Pay Violation Charge
              </h2>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentClientSecret(null);
                  setSelectedCharge(null);
                }}
                className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-smooth"
              >
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>

            <div className="mb-6 p-4 bg-muted rounded-lg">
              <p className="font-body text-sm text-muted-foreground mb-1">
                {selectedCharge?.violationReason}
              </p>
              <p className="font-heading text-2xl font-bold text-foreground">
                ${Number(selectedCharge?.chargeAmount)?.toFixed(2)}
              </p>
            </div>

            <Elements stripe={stripePromise} options={{ clientSecret: paymentClientSecret }}>
              <PaymentForm
                charge={selectedCharge}
                onSuccess={handlePaymentSuccess}
                onCancel={() => {
                  setShowPaymentModal(false);
                  setPaymentClientSecret(null);
                  setSelectedCharge(null);
                }}
              />
            </Elements>
          </div>
        </div>
      )}

      {/* Dispute Modal */}
      {showDisputeModal && selectedCharge && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-elevation-4 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-foreground">
                Dispute Charge
              </h2>
              <button
                onClick={() => {
                  setShowDisputeModal(false);
                  setDisputeReason('');
                  setSelectedCharge(null);
                }}
                className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-smooth"
              >
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>

            <div className="mb-6 p-4 bg-muted rounded-lg">
              <p className="font-body text-sm text-muted-foreground mb-1">
                {selectedCharge?.violationReason}
              </p>
              <p className="font-heading text-2xl font-bold text-foreground">
                ${Number(selectedCharge?.chargeAmount)?.toFixed(2)}
              </p>
            </div>

            <div className="mb-6">
              <label className="font-body font-medium text-foreground block mb-2">
                Reason for Dispute <span className="text-danger">*</span>
              </label>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e?.target?.value)}
                placeholder="Explain why you're disputing this charge..."
                rows={4}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth font-body text-foreground bg-background"
              />
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDisputeModal(false);
                  setDisputeReason('');
                  setSelectedCharge(null);
                }}
                disabled={isSubmittingDispute}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitDispute}
                loading={isSubmittingDispute}
                iconName="AlertTriangle"
              >
                Submit Dispute
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenterViolationCharges;